import { Typography } from '@mui/material';
import { Button } from '@globalvisioninc/ui/button';
import { Chip } from '@globalvisioninc/ui/chip';
import { Trash } from '@globalvisioninc/assets/icons';
import PDFTronManager from 'components/PDFViewer/PDFTronManager';
import { useSelector } from 'react-redux';
import {
  getAllAnnotationsLoaded,
  getAnnotations,
  getIsSingleFile,
  getManualSelectedZoneId,
  getPolygonsAreActive,
  getSelectedTool,
} from 'store';
import { PDFTool, PDFTronTools, PDFTRON_DEFAULT_TOOL } from 'types';
import { useTracker } from '../../Tracker/TrackerProvider';
import OCRTool from './OCRTool';
import TextSelectTool from './TextSelectTool';
import CrossoutTool from './CrossoutTool';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import CropTool from './CropTool';
import { resetCursors, setCropZonesAreListable } from 'pdftron/docManager/AnnotationTools/utils/CropToolUtils';
import selectTool from 'utils/selectTool/selectTool';
import { clearInputAnnotations } from 'utils/clearInputAnnotations/clearInputAnnotations';
import { useDocumentToastStateStore } from 'zstore/documentToastStateStore';
import { useFlags } from 'launchdarkly-react-client-sdk';
import styles from './AnnotationsTools.module.css';

export const toggleTool = (tool: PDFTool, selectedTool: PDFTool, manualSelectedZoneId?: number): PDFTool => {
  let currentTool = selectedTool;
  if (currentTool === tool) {
    currentTool = PDFTRON_DEFAULT_TOOL;
    resetCursors();
    setCropZonesAreListable(true);
  } else {
    currentTool = tool;
    if (tool === PDFTronTools.POLYGON || tool === PDFTronTools.CROP || tool === PDFTronTools.PAN) {
      setCropZonesAreListable(true);
    } else {
      setCropZonesAreListable(false);
    }
  }
  PDFTronManager.setToolMode(currentTool, manualSelectedZoneId);

  return currentTool;
};

const AnnotationsTools = () => {
  const annotations = useSelector(getAnnotations);
  const selectedTool = useSelector(getSelectedTool);
  const { sourcePolygonsActive, targetPolygonsActive } = useSelector(getPolygonsAreActive);
  const polygonsAreActive = sourcePolygonsActive || targetPolygonsActive;

  const manualSelectedZoneId = useSelector(getManualSelectedZoneId);
  const annotationLoaded = useSelector(getAllAnnotationsLoaded);
  const isSingleFile = useSelector(getIsSingleFile);

  const expireAllToast = useDocumentToastStateStore((s) => s.expireAllToast);

  const featureFlags = useFlags();
  const showOcr = featureFlags.showOcr;

  const listOfSelectedTools = [
    PDFTronTools.GRAPHIC,
    PDFTronTools.CROSSOUT,
    PDFTronTools.ZONE,
    PDFTronTools.MARQUEE_ZONE,
    PDFTronTools.CROP,
    PDFTronTools.POLYGON,
  ];
  const toolDescription: { [index: string]: string } = {
    [PDFTronTools.ZONE]: 'Highlight source text—matches appear automatically. Unselected text will be ignored.',
    [PDFTronTools.CROSSOUT]:
      'Strikeout the text on your documents that you wish to ignore—all other text will be inspected.',
    [PDFTronTools.GRAPHIC]:
      'Draw a selection around the graphic elements on your source document that you wish to inspect',
    [PDFTronTools.CROP]:
      'Crop the area(s) you wish to inspect on a page. Other pages will be included unless cropped or excluded.',
    [PDFTronTools.POLYGON]:
      'Crop the area(s) you wish to inspect on a page. Other pages will be included unless cropped or excluded.',
    [PDFTronTools.MARQUEE_ZONE]:
      'Draw boxes around text—compare both documents with control. Unselected text will be ignored.',
  };

  const tracker = useTracker();

  const handleSelectTool = (tool: PDFTool): void => {
    if (selectedTool !== tool) {
      tracker.track({
        name: 'annotation-tool-selected',
        data: {
          tool,
        },
      });
    }
    selectTool(tool);
  };

  const handleClearAll = () => {
    tracker.track({
      name: 'annotation-markup-all-cleared',
    });

    clearInputAnnotations();
    // expire all document toast
    expireAllToast();

    // Deselects the currently active tool
    selectTool(selectedTool);
  };

  // Exclude non graphic full page graphic annotations from count
  const clearCheck = isSingleFile
    ? annotations.target.filter((anno) => anno.graphicZone?.fullPage !== true).length > 0 || polygonsAreActive
    : annotations.source.filter((anno) => anno.graphicZone?.fullPage !== true).length > 0 ||
      annotations.target.filter((anno) => anno.graphicZone?.fullPage !== true).length > 0 ||
      polygonsAreActive;

  const isDisabled = !clearCheck || !annotationLoaded;

  return (
    <>
      <div className={styles.root}>
        {showOcr && <OCRTool handleClearAll={handleClearAll} />}
        {!isSingleFile && <TextSelectTool handleSelectTool={handleSelectTool} />}
        <CrossoutTool handleSelectTool={handleSelectTool} />
        <CropTool handleSelectTool={handleSelectTool} />
        <GVTooltip title={clearCheck ? 'Clear All Markups' : 'There are no markups to be cleared.'} side="bottom">
          <Button.Root
            mode="tertiary"
            id="clearAllZone"
            isDisabled={isDisabled}
            elementStart={<Trash />}
            onClick={handleClearAll}
          >
            Clear All
          </Button.Root>
        </GVTooltip>
      </div>
      {listOfSelectedTools.includes(selectedTool) && (
        <div className={styles.text}>
          {selectedTool === PDFTronTools.POLYGON ? (
            <>
              <Chip.Root className={styles.chip}>New</Chip.Root>
              {toolDescription[selectedTool]}
            </>
          ) : (
            <Typography variant="body1">{toolDescription[selectedTool]}</Typography>
          )}
        </div>
      )}
      {/* TODO: we need a description for crossout tool as well */}
    </>
  );
};

export default AnnotationsTools;
